import { useEffect, useState } from 'react';

// Tiny module-level event store that tracks in-flight GraphQL requests
// so the editor can show "Saving…" / "Saved" feedback. withClient
// wires the request lifecycle into recordStart / recordEnd below.
//
// We deliberately track all GraphQL requests rather than just
// mutations — autoSave debounces edits into mutations, and the user
// experience we want ("Saving…") covers any short period of network
// activity that follows their input. The noise from background
// queries is filtered by only flipping to "Saving…" when traffic
// persists for >= 200ms.

type SaveState = 'idle' | 'saving' | 'saved';

const listeners = new Set<() => void>();
let inFlight = 0;
let lastSaved: number | null = null;
let savingTimer: ReturnType<typeof setTimeout> | null = null;
let showSavingLatched = false;

function emit() {
  listeners.forEach(fn => fn());
}

export function recordRequestStart(): void {
  inFlight += 1;
  if (inFlight === 1) {
    // Only show "Saving…" if requests take longer than 200ms — keeps
    // a steady stream of fast queries from making the badge flicker.
    savingTimer = setTimeout(() => {
      if (inFlight > 0) {
        showSavingLatched = true;
        emit();
      }
    }, 200);
  }
  emit();
}

export function recordRequestEnd(): void {
  inFlight = Math.max(0, inFlight - 1);
  if (inFlight === 0) {
    if (savingTimer) {
      clearTimeout(savingTimer);
      savingTimer = null;
    }
    if (showSavingLatched) {
      lastSaved = Date.now();
    }
    showSavingLatched = false;
  }
  emit();
}

export function useSaveStatus(): {
  state: SaveState;
  lastSavedAt: number | null;
} {
  const [, force] = useState(0);

  useEffect(() => {
    const fn = () => force(n => n + 1);

    listeners.add(fn);

    return () => {
      listeners.delete(fn);
    };
  }, []);

  // Tick once a minute so "saved Xm ago" relative time stays fresh.
  useEffect(() => {
    const id = setInterval(() => force(n => n + 1), 30000);

    return () => clearInterval(id);
  }, []);

  if (showSavingLatched && inFlight > 0) {
    return { lastSavedAt: lastSaved, state: 'saving' };
  }
  if (lastSaved !== null) {
    return { lastSavedAt: lastSaved, state: 'saved' };
  }

  return { lastSavedAt: null, state: 'idle' };
}

export function relativeSavedLabel(ts: number | null): string {
  if (!ts) return '';
  const diff = Math.floor((Date.now() - ts) / 1000);

  if (diff < 5) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  const min = Math.floor(diff / 60);

  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);

  return `${hr}h ago`;
}
